import { shallowRef, type ShallowRef, watch } from "vue";
import { getCurrentUser } from "vuefire";
import { ZodSchema } from "zod";
import { i18n } from "@acme/i18n";
import { RecaptchaErrorResponseSchema } from "./schemas/user";
import { toast } from "vue-sonner";

export const AUTH_ENABLED = import.meta.env.VITE_FEATURE_AUTH === "true";
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
export const imageServerURL = "http://localhost:8081";

export type QueryValue =
  | string
  | number
  | boolean
  | undefined
  | Record<string, string | number | boolean | undefined>;

export type QueryParams = Record<string, QueryValue>;
export interface FileDownload {
  blob: Blob;
  filename?: string;
}
export interface FetchOptions<T> extends RequestInit {
  schema?: ZodSchema<T>;
  queryParams?: QueryParams;
}
export class Success<T, Status extends number = number> {
  readonly success = true;
  readonly failure = false;
  readonly loading = false;

  constructor(
    readonly content: T,
    readonly status: Status,
  ) {}
}
export class Failure {
  readonly success = false;
  readonly failure = true;
  readonly loading = false;

  constructor(
    readonly message: string,
    readonly isWarning: boolean = false,
  ) {}
}

export class Loading {
  readonly success = false;
  readonly failure = false;
  constructor(readonly loading = true) {}
}
export type Result<T> = Success<T> | Failure;

export interface ResultOkRef<
  T extends Success<unknown, number>,
> extends ShallowRef<T | Failure | Loading> {
  refresh: (queryParams?: QueryParams) => Promise<void>;
}

//@ts-ignore
const { t } = i18n.global;
const THROTTLE_TOAST_ID = "api-throttled";

function getRetryAfterSeconds(response: Response): number | undefined {
  const retryAfter = response.headers.get("Retry-After");
  if (!retryAfter) return undefined;

  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.max(1, Math.ceil(seconds));
  }

  const retryAt = Date.parse(retryAfter);
  if (Number.isNaN(retryAt)) return undefined;
  return Math.max(1, Math.ceil((retryAt - Date.now()) / 1000));
}

function notifyThrottled(response: Response) {
  if (typeof window === "undefined") return;

  const retryAfterSeconds = getRetryAfterSeconds(response);
  toast.error(t("api.throttled"), {
    id: THROTTLE_TOAST_ID,
    description:
      retryAfterSeconds === undefined
        ? t("api.throttledDesc")
        : t("api.throttledDescWithRetry", { seconds: retryAfterSeconds }),
  });
}

class ApiService {
  private _baseUrl = API_BASE_URL;

  public get baseUrl() {
    return this._baseUrl;
  }
  public set baseUrl(value) {
    this._baseUrl = value;
  }

  private buildQueryString(queryParams: QueryParams | undefined): string {
    if (!queryParams) return "";
    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(queryParams)) {
      if (value === undefined) continue;

      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        for (const [nestedKey, nestedValue] of Object.entries(value)) {
          if (nestedValue === undefined) continue;
          searchParams.append(`${key}[${nestedKey}]`, String(nestedValue));
        }
      } else {
        searchParams.append(key, String(value));
      }
    }

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : "";
  }

  private async buildAuthHeaders(headers?: HeadersInit): Promise<HeadersInit> {
    if (!AUTH_ENABLED) {
      return headers ?? {};
    }

    const currentUser = await getCurrentUser();
    const accessToken = await currentUser?.getIdToken();
    if (!accessToken) {
      return headers ?? {};
    }

    return {
      ...(headers ?? {}),
      Authorization: accessToken,
    };
  }

  private buildUrl(endpoint: string, queryParams?: QueryParams): string {
    return `${this.baseUrl}${endpoint}${this.buildQueryString(queryParams)}`;
  }

  private async getErrorMessage(response: Response): Promise<Failure> {
    if (response.status === 404) {
      return new Failure(t("api.notFound"), true);
    }

    if (response.status === 429) {
      notifyThrottled(response);
      return new Failure(t("api.throttled"));
    }

    if (response.status === 401 || response.status === 403) {
      return new Failure(t("api.unauthorized"));
    }
    const errorBody = await response.json();
    if (RecaptchaErrorResponseSchema.safeParse(errorBody).success) {
      return new Failure(t("api.recaptchaFailed"));
    }

    return new Failure(t("api.somethingWentWrong"));
  }

  private async request<T>(
    endpoint: string,
    options: FetchOptions<T>,
  ): Promise<Result<T>> {
    const { schema, queryParams, ...fetchOptions } = options;
    const url = this.buildUrl(endpoint, queryParams);

    fetchOptions.headers = await this.buildAuthHeaders(fetchOptions.headers);

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      return await this.getErrorMessage(response);
    }

    if (response.status === 204) {
      return new Success({} as T, 204);
    }

    const rawData = await response.json();

    if (!schema) {
      return new Success(rawData as T, response.status);
    }

    const parsedData = schema.safeParse(rawData);
    if (!parsedData.success) {
      console.error("Failed to parse response:", parsedData.error);
      return new Failure(t("api.invalidData"));
    }

    return new Success(parsedData.data, response.status);
  }

  async get<T>(
    endpoint: string,
    schema: ZodSchema<T>,
    queryParams?: QueryParams,
  ): Promise<Result<T>> {
    return this.request<T>(endpoint, {
      method: "GET",
      schema,
      queryParams,
    });
  }

  async post<T>(
    endpoint: string,
    schema: ZodSchema<T>,
    body: unknown,
  ): Promise<Result<T>> {
    return this.request<T>(endpoint, {
      method: "POST",
      schema,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
  }

  async delete(
    endpoint: string,
    queryParams?: QueryParams,
  ): Promise<Result<void>> {
    return this.request<void>(endpoint, {
      method: "DELETE",
      queryParams,
    });
  }

  async patch<T>(
    endpoint: string,
    schema: ZodSchema<T>,
    body: unknown,
  ): Promise<Result<T>> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      schema,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
  }

  async postFormData<T>(
    endpoint: string,
    body: FormData,
    schema?: ZodSchema<T>,
  ): Promise<Result<T>> {
    return this.request<T>(endpoint, {
      method: "POST",
      schema,
      body,
    });
  }

  async getBlob(
    endpoint: string,
    queryParams?: QueryParams,
  ): Promise<Result<FileDownload>> {
    const url = this.buildUrl(endpoint, queryParams);
    const headers = await this.buildAuthHeaders();
    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      return await this.getErrorMessage(response);
    }

    const contentDisposition =
      response.headers.get("Content-Disposition") || "";
    const filenameMatch = contentDisposition.match(
      /filename\*?=(?:UTF-8''|\")?([^;\"]+)/i,
    );
    const filename = filenameMatch?.[1]?.trim();
    const blob = await response.blob();
    return new Success({ blob, filename }, response.status);
  }
}

export const api = new ApiService();

export function watchRequest<T>(
  endpoint: string,
  schema: ZodSchema<T>,
  queryParams?: QueryParams,
): ResultOkRef<Success<T>> {
  const state = shallowRef<Success<T> | Failure | Loading>(new Loading());
  let currentQueryParams = queryParams;

  const fetchData = async (nextQueryParams?: QueryParams) => {
    if (nextQueryParams !== undefined) {
      currentQueryParams = nextQueryParams;
    }
    state.value = new Loading();

    const result = await api.get<T>(endpoint, schema, currentQueryParams);

    if (result.success) {
      state.value = new Success(result.content, result.status);
    } else {
      state.value = result;
    }
  };

  if (!import.meta.env.SSR) {
    fetchData();
  }

  watch(
    () => queryParams,
    () => {
      currentQueryParams = queryParams;
      fetchData();
    },
    { deep: true },
  );

  return Object.assign(state, {
    refresh: (nextQueryParams?: QueryParams) => fetchData(nextQueryParams),
  });
}
